import type { Liability } from '../types'

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

export function formatPercent(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(value / 100)
}

export function calculateLTV(
  loanAmount: number,
  propertyValue: number,
): number {
  if (propertyValue === 0) return 0
  return (loanAmount / propertyValue) * 100
}

export function calculateMonthlySavings(
  currentPayment: number,
  proposedPayment: number,
): number {
  return currentPayment - proposedPayment
}

// Total balance of liabilities marked as being paid off via the refinance
export function sumLiabilitiesPaidOff(liabilities: Liability[]): number {
  return liabilities
    .filter((l) => l.beingPaidOff)
    .reduce((sum, l) => sum + (l.currentBalance || 0), 0)
}

// Sum of ALL monthly liability payments, regardless of paid-off status
export function sumAllMonthlyLiabilityPayments(
  liabilities: Liability[],
): number {
  return liabilities.reduce((sum, l) => sum + (l.monthlyPayment || 0), 0)
}

// Sum of monthly liability payments that REMAIN after the refi (not being paid off)
export function sumRemainingMonthlyLiabilityPayments(
  liabilities: Liability[],
): number {
  return liabilities
    .filter((l) => !l.beingPaidOff)
    .reduce((sum, l) => sum + (l.monthlyPayment || 0), 0)
}

// Total monthly outlay = mortgage P&I + relevant monthly liability payments
export function calculateCurrentTotalMonthlyPayment(
  currentPayment: number,
  liabilities: Liability[],
): number {
  return currentPayment + sumAllMonthlyLiabilityPayments(liabilities)
}

export function calculateProposedTotalMonthlyPayment(
  proposedPayment: number,
  liabilities: Liability[],
): number {
  return proposedPayment + sumRemainingMonthlyLiabilityPayments(liabilities)
}
